<template>
  <div class="login-page">
    <div class="login-page__main">
      <img src="@shared/assets/img/logo.png" alt="Funnelflow" class="login-page__logo" />

      <div class="login-page__wrap">
        <h1 class="login-page__title">{{ $t('auth.welcome_back') }}</h1>
        <p class="login-page__subtitle">{{ $t('auth.login_subtitle') }}</p>

        <form @submit.prevent="login" v-if="useAuth" class="login-page__form">
          <TextInput
            :id="inputs.email.id"
            :label="inputs.email.label"
            v-model="inputs.email.binding"
            type="email"
          />
          <div class="login-page__password">
            <TextInput
              :id="inputs.password.id"
              :label="inputs.password.label"
              v-model="inputs.password.binding"
              type="password"
            />
            <button type="button" @click="resetPassword()" class="login-page__forgot-password">
              {{ $t('auth.forgot_password') }}
            </button>
          </div>
          <Btn
            type="submit"
            :loading="isLoading"
            variant="secondary"
            size="lg"
            class="login-page__submit"
            :text="$t('auth.sign_in')"
          />
        </form>

        <div class="login-page__separator">
          <span>{{ $t('auth.or') }}</span>
        </div>

        <GoogleAuthButton
          mode="auth"
          @connected="handleGoogleAuthSuccess"
          @error="handleGoogleAuthError"
          :loading="isGoogleAuthLoading"
          class="width-100"
        />
      </div>
    </div>

    <div class="login-page__aside">
      <LoginShowcase />
    </div>
  </div>
</template>

<script>
  import { useRouter, useRoute } from 'vue-router';
  import { ref, getCurrentInstance } from 'vue';
  import { supabase } from '@/services/supabase';
  import { useUserStore } from '@/store/userStore';
  import { useUIStore } from '@/store/uiStore';

  import GoogleAuthButton from '@/components/inputs/GoogleAuthButton.vue';
  import TextInput from '@/components/inputs/TextInput.vue';
  import Btn from '@/components/partials/Btn.vue';
  import LoginShowcase from '@/components/authentication/LoginShowcase.vue';

  export default {
    components: {
      TextInput,
      Btn,
      GoogleAuthButton,
      LoginShowcase,
    },
    setup() {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;
      const router = useRouter();
      const route = useRoute();
      const userStore = useUserStore();
      const uiStore = useUIStore();
      const useAuth = import.meta.env.VITE_USE_AUTH === 'true';
      const isLoading = ref(false);
      const isGoogleAuthLoading = ref(false);

      const inputs = ref({
        email: {
          id: 'email',
          label: $t('auth.email'),
          binding: '',
          error: '',
        },
        password: {
          id: 'password',
          label: $t('auth.password'),
          binding: '',
          error: '',
        },
      });

      const login = async () => {
        isLoading.value = true;
        try {
          await userStore.login(inputs.value.email.binding, inputs.value.password.binding);

          // Honor a safe internal redirect first (e.g. returning to an invite
          // the user was accepting). This must come BEFORE the no-org check —
          // an org-less user logging in to accept an invite needs to reach it.
          const redirect = route.query.redirect;
          if (
            typeof redirect === 'string' &&
            redirect.startsWith('/') &&
            !redirect.startsWith('//')
          ) {
            await router.push(redirect);
            return;
          }

          // A user with no accessible organization (e.g. removed from their only
          // team) can authenticate but has nowhere to land. Don't navigate into
          // the app — that would flash a page and get bounced straight back by
          // the auth interceptor. Keep them here with a clear message instead.
          if (!userStore.currentOrganization) {
            await userStore.logout();
            uiStore.addModal(null, {
              title: $t('auth.no_organization_title'),
              description: $t('auth.no_organization_description'),
            });
            return;
          }

          const role = userStore.currentOrganization?.role;
          const destination =
            role === 'installer'
              ? '/vandaag'
              : role === 'admin' || role === 'super_admin'
                ? '/home'
                : '/all-flows';
          await router.push(destination);
        } catch (error) {
          uiStore.addModal(null, {
            title: $t('auth.login_error_title'),
            description: $t('auth.login_error_description'),
          });
        } finally {
          isLoading.value = false;
        }
      };

      const resetPassword = async () => {
        try {
          if (!inputs.value.email.binding) {
            uiStore.addModal(null, {
              title: $t('auth.reset_email_required_title'),
              description: $t('auth.reset_email_required_description'),
            });
            return;
          }

          const redirectUrl = `${window.location.origin}/reset-password`;

          const { error } = await supabase.auth.resetPasswordForEmail(
            inputs.value.email.binding,
            { redirectTo: redirectUrl }
          );

          if (error) {
            if (error.message.includes('rate limit')) {
              uiStore.addModal(null, {
                title: $t('auth.reset_rate_limit_title'),
                description: $t('auth.reset_rate_limit_description'),
              });
            } else {
              uiStore.addModal(null, {
                title: $t('auth.reset_error_title'),
                description: error.message || $t('auth.reset_error_description'),
              });
            }
            return;
          }

          uiStore.addModal(null, {
            title: $t('auth.reset_sent_title'),
            description: $t('auth.reset_sent_description'),
          });
        } catch (error) {
          uiStore.addModal(null, {
            title: $t('auth.login_error_title'),
            description: error.message || $t('auth.reset_error_description'),
          });
        }
      };

      const navigateToFormBuilder = () => {
        router.push('/form-builder');
      };

      const handleGoogleAuthSuccess = async (data) => {
        isGoogleAuthLoading.value = true;
        try {
          console.log('Google auth initiated');
          // await userStore.fetchUserData();
          // router.push('/all-flows');
        } catch (error) {
          console.error('Error handling auth success:', error);
        }
      };

      const handleGoogleAuthError = (error) => {
        console.error('Google auth error:', error);
        alert(error.message);
      };

      return {
        inputs,
        isLoading,
        login,
        resetPassword,
        useAuth,
        navigateToFormBuilder,
        handleGoogleAuthSuccess,
        handleGoogleAuthError,
        isGoogleAuthLoading,
      };
    },
  };
</script>

<style scoped lang="scss">
  @import '@/assets/scss/base/mixins';
  .login-page {
    display: flex;
    min-height: 100vh;
    background-color: var(--white);

    &__main {
      position: relative;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      width: 50%;
      padding: 6rem 2rem 4rem;
      @include lg-down {
        width: 100%;
        min-height: 100vh;
        padding: 5rem 1.25rem 2rem;
      }
    }

    &__logo {
      position: absolute;
      top: 2rem;
      left: 2.5rem;
      height: 32px;
      width: auto;
      @include lg-down {
        left: 1.25rem;
      }
    }

    &__wrap {
      width: 380px;
      max-width: 100%;
    }

    &__title {
      font-size: 1.75rem;
      font-weight: 600;
      letter-spacing: -0.02em;
      color: var(--slate-900);
    }

    &__subtitle {
      @include fontSize('md');
      margin: 0.375rem 0 2rem;
      color: var(--slate-500);
    }

    &__form {
      width: 100%;
    }

    &__password {
      position: relative;
    }

    &__forgot-password {
      @include fontSize('sm');
      position: absolute;
      top: 0.5rem;
      right: 0;
      line-height: 21px;
      padding: 0;
      border: 0;
      background: none;
      color: var(--slate-500);
      cursor: pointer;
      @include hover {
        color: var(--slate-900);
        text-decoration: underline;
      }
    }

    &__submit {
      width: 100%;
      margin-top: 0.5rem;
    }

    &__separator {
      @include fontSize('sm');
      display: flex;
      align-items: center;
      gap: 0.75rem;
      margin: 1.5rem 0;
      color: var(--slate-400);
      &::before,
      &::after {
        content: '';
        flex: 1;
        height: 1px;
        background-color: var(--slate-200);
      }
    }

    &__aside {
      display: flex;
      width: 50%;
      padding: 1rem;
      @include lg-down {
        display: none;
      }
    }

  }
</style>
